import React, { useState, useRef, useEffect } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import {
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalTitle,
  Header,
  ButtonGroup,
  CancelButton,
  ConfirmButton,
  Row,
  Label,
  Column,
  AspectRations,
  AspectRationButton,
  ZoomSlider,
  CropContainer,
} from "./CropModal.styles";
import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { ASPECT_RATIONS } from "../../utils/constants";
import { useMediaQuery } from "react-responsive";

const CropModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedImage,
  confirmButtonType = "primary",
  isLoading = false,
}) => {
  const [selectedAspect, setSelectedAspect] = useState(ASPECT_RATIONS[0]);
  const [crop, setCrop] = useState({
    unit: "%",
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  });
  const [completedCrop, setCompletedCrop] = useState();
  const [zoom, setZoom] = useState(1);
  const imgRef = useRef(null);

  useLockBodyScroll(isOpen);

  const isMobile = useMediaQuery({ maxWidth: 768 });

  // Reset crop when aspect ratio or selected image changes
  useEffect(() => {
    if (imgRef.current) {
      const aspect = selectedAspect.value;
      setZoom(1); // Reset zoom when aspect ratio changes
      setCompletedCrop(); // Reset completed crop

      if (aspect) {
        const imgWidth = imgRef.current.width;
        const imgHeight = imgRef.current.height;
        const imgAspect = imgWidth / imgHeight;

        if (imgAspect > aspect) {
          // Image is wider than aspect ratio
          const cropWidth = (aspect / imgAspect) * 100;
          setCrop({
            unit: "%",
            x: (100 - cropWidth) / 2,
            y: 0,
            width: cropWidth,
            height: 100,
          });
        } else {
          // Image is taller than aspect ratio
          const cropHeight = (imgAspect / aspect) * 100;
          setCrop({
            unit: "%",
            x: 0,
            y: (100 - cropHeight) / 2,
            width: 100,
            height: cropHeight,
          });
        }
      } else {
        // Original - full image
        setCrop({
          unit: "%",
          x: 0,
          y: 0,
          width: 100,
          height: 100,
        });
      }
    }
  }, [selectedAspect, selectedImage?.url]);

  const onImageLoad = (e) => {
    imgRef.current = e.currentTarget;
    setZoom(1); // Reset zoom on image load

    // Set initial crop to full image
    setCrop({
      unit: "%",
      x: 0,
      y: 0,
      width: 100,
      height: 100,
    });
  };

  const getCroppedImg = async () => {
    // Use current crop if completedCrop is not available
    const cropToUse = completedCrop || crop;
    if (!cropToUse || !imgRef.current) return null;

    const image = imgRef.current;
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Convert crop coordinates to pixels and account for zoom
    let cropX, cropY, cropWidth, cropHeight;

    // Convert crop coordinates to percentages if they're in pixels
    let normalizedCrop = cropToUse;
    if (cropToUse.unit === "px") {
      normalizedCrop = {
        x: (cropToUse.x / image.width) * 100,
        y: (cropToUse.y / image.height) * 100,
        width: (cropToUse.width / image.width) * 100,
        height: (cropToUse.height / image.height) * 100,
        unit: "%",
      };
    }

    // When zoomed, we need to adjust for the zoom center point
    if (zoom > 1) {
      // Calculate the center offset for zoom
      const zoomCenterOffsetX = ((zoom - 1) / 2) * 100;
      const zoomCenterOffsetY = ((zoom - 1) / 2) * 100;

      // Adjust crop coordinates for zoom center
      const zoomAdjustedX = (normalizedCrop.x + zoomCenterOffsetX) / zoom;
      const zoomAdjustedY = (normalizedCrop.y + zoomCenterOffsetY) / zoom;
      const zoomAdjustedWidth = normalizedCrop.width / zoom;
      const zoomAdjustedHeight = normalizedCrop.height / zoom;

      cropX = (zoomAdjustedX / 100) * image.width * scaleX;
      cropY = (zoomAdjustedY / 100) * image.height * scaleY;
      cropWidth = (zoomAdjustedWidth / 100) * image.width * scaleX;
      cropHeight = (zoomAdjustedHeight / 100) * image.height * scaleY;
    } else {
      // No zoom, use original coordinates
      cropX = (normalizedCrop.x / 100) * image.width * scaleX;
      cropY = (normalizedCrop.y / 100) * image.height * scaleY;
      cropWidth = (normalizedCrop.width / 100) * image.width * scaleX;
      cropHeight = (normalizedCrop.height / 100) * image.height * scaleY;
    }

    canvas.width = cropWidth;
    canvas.height = cropHeight;

    const ctx = canvas.getContext("2d");

    // Apply image smoothing for better quality
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight,
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(null);
            return;
          }
          const croppedImageUrl = URL.createObjectURL(blob);
          resolve({ blob, url: croppedImageUrl });
        },
        "image/jpeg",
        0.95,
      );
    });
  };

  const handleConfirm = async () => {
    const croppedImage = await getCroppedImg();
    if (croppedImage && onConfirm) {
      onConfirm(croppedImage);
    }
    onClose();
  };

  const handleCancel = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleAspectChange = (ration) => {
    setSelectedAspect(ration);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      customOverlayStyles={{ zIndex: 5100 }}
    >
      <ModalContainer>
        <Header>
          <ModalTitle>Crop Image</ModalTitle>
          <CloseButton onClick={handleCancel}>
            <CloseSvg width={16} height={16} />
          </CloseButton>
        </Header>

        <ModalContent>
          <CropContainer>
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={selectedAspect.value}
            >
              <img
                ref={imgRef}
                src={selectedImage?.url}
                onLoad={onImageLoad}
                alt="Crop preview"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  maxHeight: isMobile ? "280px" : "380px",
                  display: "block",
                  transform: `scale(${zoom})`,
                  transformOrigin: "center",
                }}
              />
            </ReactCrop>
          </CropContainer>

          <Column>
            <Label>Aspect Ratio</Label>
            <AspectRations>
              {ASPECT_RATIONS.map((ration) => (
                <AspectRationButton
                  key={ration.id}
                  $active={selectedAspect.id === ration.id}
                  onClick={() => handleAspectChange(ration)}
                >
                  {ration.label}
                </AspectRationButton>
              ))}
            </AspectRations>
          </Column>

          <Column>
            <Row>
              <Label>Zoom</Label>
              <Label style={{ opacity: 0.6 }}>{Math.round(zoom * 100)}%</Label>
            </Row>
            <ZoomSlider
              type="range"
              min={0.5}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
            />
          </Column>

          <ButtonGroup>
            <CancelButton onClick={handleCancel} disabled={isLoading}>
              Cancel
            </CancelButton>
            <ConfirmButton
              onClick={handleConfirm}
              $type={confirmButtonType}
              disabled={isLoading}
            >
              {isLoading ? (
                <l-bouncy size="20" stroke="2" speed="1" color="#fff" />
              ) : (
                "Crop"
              )}
            </ConfirmButton>
          </ButtonGroup>
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};

export default CropModal;
